/**
 * HistoryRiskAssessmentPanel.js
 *
 * Clinician risk assessment step at the end of Adult Autism History. Reuses the
 * same categories, Yes/No toggles, creatable detail dropdowns, and additional
 * notes as ADHD 11_RSK.js, but styled to match AdultAutismHistory subsection
 * panels (grey rounded cards, border-t tables, DictationTextArea notes block).
 *
 * Writes to assessmentStages/history.riskAssessment on the modular pathway.
 */

import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { ExclamationIcon } from '@heroicons/react/solid';
import * as AlertDialog from '@radix-ui/react-alert-dialog';
import AutoSaveCreatable from '../../../../components/AutoSaveCreatable';
import StructuredNotesEditor, { normaliseStructuredNotes } from '../../12_observation/components/StructuredNotesEditor';
import {
  HISTORY_RISK_QUESTION_PANELS,
  RISK_ADDITIONAL_NOTES_PANEL_KEY,
} from './historyRiskAssessmentConfig';
import getScrollablePortalTarget from '../../../../utils/getScrollablePortalTarget';

const SELECT_STYLES = {
  menu: (base) => ({ ...base, textAlign: 'left' }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  option: (base, state) => ({
    ...base,
    textAlign: 'left',
    backgroundColor: state.isSelected ? 'lightgray' : base.backgroundColor,
  }),
};

// Same portal as patient-setup / reassign dialogs. This panel's Yes/No
// confirm is a Radix AlertDialog, which sets body { pointer-events: none }
// and locks scroll; menus portaled straight to document.body inherit both.
const RISK_SELECT_PORTAL_PROPS = {
  classNamePrefix: 'td-select',
  menuPortalTarget: getScrollablePortalTarget(),
  menuPosition: 'fixed',
};

export default function HistoryRiskAssessmentPanel({
  riskAssessmentData,
  onRiskAssessmentUpdate,
  noteMoveTargets = null,
  onMovePanelNote = null,
  canEdit,
  // Optional tag options for the Additional Notes editor. Autism callers
  // omit it (the editor keeps its default); the CYP CI page passes the
  // parent/child/school source tags so risk-note attribution survives into
  // the LLM inputs (ASMT-29).
  noteTagOptions = undefined,
}) {
  const questionPanels = HISTORY_RISK_QUESTION_PANELS;

  const initializeSelections = useCallback((riskData) => {
    const subsections = riskData?.subsections || {};
    return questionPanels.map((panel, panelIndex) => (
      Object.entries(panel.categories).reduce((acc, [categoryName]) => {
        const categoryData = subsections[String(panelIndex + 1)]?.categories?.[categoryName] || {};
        acc[categoryName] = {
          present: categoryData.isPresentSelection ?? null,
          selectedOption: (categoryData.selectionDetails || []).map((option) => ({
            value: option,
            label: option,
          })),
        };
        return acc;
      }, {})
    ));
  }, [questionPanels]);

  const [selections, setSelections] = useState(() => initializeSelections(riskAssessmentData));
  // Additional notes are stored as a structured note array (bullet notes), the
  // same format as the History subsection notes. normaliseStructuredNotes coerces
  // any legacy plain-string value from older documents into an array on read.
  const [additionalNotes, setAdditionalNotes] = useState(
    () => normaliseStructuredNotes(riskAssessmentData?.clinicianNotes?.['Additional Notes']),
  );

  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [pendingToggle, setPendingToggle] = useState(null);

  // Re-sync when parent refetches (e.g. page refresh) without clobbering
  // in-progress edits from our own writes.
  const lastExternalRef = useRef(riskAssessmentData);
  useEffect(() => {
    if (lastExternalRef.current === riskAssessmentData) return;
    lastExternalRef.current = riskAssessmentData;
    setSelections(initializeSelections(riskAssessmentData));
    setAdditionalNotes(normaliseStructuredNotes(riskAssessmentData?.clinicianNotes?.['Additional Notes']));
  }, [riskAssessmentData, initializeSelections]);

  const buildCategoryFirestorePatch = useCallback((panelIndex, categoryName, isPresent, selectionDetails) => ({
    subsections: {
      [String(panelIndex + 1)]: {
        categories: {
          [categoryName]: {
            isPresentSelection: isPresent,
            selectionDetails,
          },
        },
      },
    },
  }), []);

  const hasSelectionsToLose = useCallback((panelIndex, categoryName) => {
    const existing = selections[panelIndex]?.[categoryName]?.selectedOption || [];
    if (existing.length === 0) return false;

    const currentPresent = selections[panelIndex]?.[categoryName]?.present;
    if (currentPresent === null) return true;

    if (currentPresent === false) {
      const defaultNo = questionPanels[panelIndex].categories[categoryName].noOptions[0];
      return !(existing.length === 1 && existing[0].value === defaultNo);
    }

    return true;
  }, [selections, questionPanels]);

  const executeSelection = useCallback((panelIndex, categoryName, isPresent) => {
    setSelections((prev) => {
      const next = [...prev];
      next[panelIndex] = {
        ...next[panelIndex],
        [categoryName]: {
          present: isPresent,
          selectedOption: isPresent === false
            ? [{
              value: questionPanels[panelIndex].categories[categoryName].noOptions[0],
              label: questionPanels[panelIndex].categories[categoryName].noOptions[0],
            }]
            : [],
        },
      };
      return next;
    });

    const selectionDetails = isPresent === false
      ? [questionPanels[panelIndex].categories[categoryName].noOptions[0]]
      : [];

    onRiskAssessmentUpdate(buildCategoryFirestorePatch(
      panelIndex,
      categoryName,
      isPresent,
      selectionDetails,
    ));
  }, [questionPanels, onRiskAssessmentUpdate, buildCategoryFirestorePatch]);

  const handleSelection = useCallback((panelIndex, categoryName, isPresent) => {
    if (!canEdit) return;

    const currentPresent = selections[panelIndex]?.[categoryName]?.present;
    const newValue = currentPresent === isPresent ? null : isPresent;

    if (hasSelectionsToLose(panelIndex, categoryName)) {
      setPendingToggle({ type: 'single', panelIndex, categoryName, isPresent: newValue });
      setConfirmDialogOpen(true);
      return;
    }

    executeSelection(panelIndex, categoryName, newValue);
  }, [canEdit, selections, hasSelectionsToLose, executeSelection]);

  const handleOptionChange = useCallback((panelIndex, categoryName, selectedOptions) => {
    if (!canEdit) return;

    setSelections((prev) => {
      const next = [...prev];
      next[panelIndex] = {
        ...next[panelIndex],
        [categoryName]: {
          ...next[panelIndex][categoryName],
          selectedOption: selectedOptions,
        },
      };
      return next;
    });

    onRiskAssessmentUpdate(buildCategoryFirestorePatch(
      panelIndex,
      categoryName,
      selections[panelIndex][categoryName].present,
      selectedOptions.map((option) => option.value),
    ));
  }, [canEdit, selections, onRiskAssessmentUpdate, buildCategoryFirestorePatch]);

  const executeNoToAll = useCallback((panelIndex) => {
    setSelections((prev) => {
      const next = [...prev];
      next[panelIndex] = Object.entries(questionPanels[panelIndex].categories).reduce((acc, [categoryName, category]) => {
        acc[categoryName] = {
          present: false,
          selectedOption: [{ value: category.noOptions[0], label: category.noOptions[0] }],
        };
        return acc;
      }, {});
      return next;
    });

    onRiskAssessmentUpdate({
      subsections: {
        [String(panelIndex + 1)]: {
          categories: Object.entries(questionPanels[panelIndex].categories).reduce((acc, [categoryName, category]) => {
            acc[categoryName] = {
              isPresentSelection: false,
              selectionDetails: [category.noOptions[0]],
            };
            return acc;
          }, {}),
        },
      },
    });
  }, [questionPanels, onRiskAssessmentUpdate]);

  const handleNoToAll = useCallback((panelIndex) => {
    if (!canEdit) return;

    const anyMeaningful = Object.keys(questionPanels[panelIndex].categories).some(
      (catName) => hasSelectionsToLose(panelIndex, catName),
    );

    if (anyMeaningful) {
      setPendingToggle({ type: 'noToAll', panelIndex });
      setConfirmDialogOpen(true);
      return;
    }

    executeNoToAll(panelIndex);
  }, [canEdit, questionPanels, hasSelectionsToLose, executeNoToAll]);

  const handleConfirmToggle = () => {
    if (!pendingToggle) return;

    if (pendingToggle.type === 'single') {
      executeSelection(pendingToggle.panelIndex, pendingToggle.categoryName, pendingToggle.isPresent);
    } else if (pendingToggle.type === 'noToAll') {
      executeNoToAll(pendingToggle.panelIndex);
    }

    setPendingToggle(null);
    setConfirmDialogOpen(false);
  };

  // Persist immediately on every note commit (add/edit/delete/reorder), matching
  // how the category selections above already save — there is no separate blur
  // step, so nothing can be lost between typing and navigating away.
  const handleAdditionalNotesChange = (notes) => {
    if (!canEdit) return;
    setAdditionalNotes(notes);
    onRiskAssessmentUpdate({
      clinicianNotes: { 'Additional Notes': notes },
    });
  };

  const activeRisks = useMemo(() => {
    const risks = [];
    questionPanels.forEach((panel, panelIndex) => {
      Object.keys(panel.categories).forEach((categoryName) => {
        if (selections[panelIndex]?.[categoryName]?.present === true) {
          risks.push(categoryName);
        }
      });
    });
    return risks;
  }, [selections, questionPanels]);

  const hasActiveRisks = activeRisks.length > 0;

  return (
    <div>
      {/* Page-level heading — matches the History section panels which each show
          the section title (e.g. "Your Wellbeing") at the top of the content area. */}
      <h2 className="text-3xl font-bold text-gray-900 text-left mb-6">Risk Assessment</h2>

      {/* Risk banner — always mounted; height/opacity animate like ADHD 11_RSK.js
          so the warning slides open/closed instead of popping in instantly. */}
      <div
        data-testid="risk-banner-shell"
        className={`overflow-hidden transition-all duration-400 ease-in-out ${
          hasActiveRisks ? 'max-h-96 mb-6 opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <div className="bg-red-50 rounded-lg border border-red-200 p-4 text-left">
          <div className="flex items-start">
            <ExclamationIcon className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" aria-hidden="true" />
            <div className="ml-3">
              <h3 className="text-sm font-semibold text-red-800">For Consideration</h3>
              <div className="mt-2 text-sm text-red-700 space-y-1">
                <p>
                  You have selected &quot;Yes&quot; to the following risks:{' '}
                  <span className="font-semibold">{activeRisks.join(', ')}</span>
                </p>
                <p>Please consider a risk management plan for this patient.</p>
                <p>
                  Risk management functionality is not yet fully supported by the ThinkDivergent platform.
                </p>
                <p className="font-medium">
                  By continuing, you acknowledge this notice and take responsibility for appropriate clinical action.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="space-y-6">
      {questionPanels.map((panel, panelIndex) => {
        const categoryNames = Object.keys(panel.categories);
        const allAlreadyNo = categoryNames.length > 0 && categoryNames.every(
          (catName) => selections[panelIndex]?.[catName]?.present === false,
        );
        const noToAllDisabled = !canEdit || allAlreadyNo;

        return (
          <div
            key={panel.title}
            className="bg-gray-50 rounded-lg border border-gray-300 text-left p-6 shadow-sm"
          >
            {/* Panel header row — mirrors ADHD 11_RSK.js: title left, "No to all?"
                centred above the Present? column, not stacked inside the table header. */}
            <div className="flex justify-between items-center mb-4">
              <div className="w-[30%] min-w-0">
                <h4 className="font-bold text-lg">{panel.title}</h4>
              </div>
              <div className="w-[20%] flex justify-center">
                <button
                  type="button"
                  onClick={() => handleNoToAll(panelIndex)}
                  className={`py-1.5 px-3 rounded-md shadow-sm font-semibold text-xs text-gray-900 bg-white border border-gray-400 transition-all duration-100 ${
                    noToAllDisabled
                      ? 'opacity-60 cursor-not-allowed'
                      : 'hover:bg-red-700 hover:text-white hover:border-red-700'
                  }`}
                  disabled={noToAllDisabled}
                >
                  No to all?
                </button>
              </div>
              <div className="w-[50%]" aria-hidden="true" />
            </div>

            <div className="overflow-x-auto">
              <table className="w-full table-auto min-w-[640px]">
                <thead>
                  <tr className="text-sm text-gray-600 border-t border-gray-200">
                    <th className="text-left font-semibold py-2 w-[30%]">Category</th>
                    <th className="text-center font-semibold py-2 w-[20%]">Present?</th>
                    <th className="text-left font-semibold py-2 w-[50%]">Details</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(panel.categories).map(([categoryName, category]) => (
                    <tr key={categoryName} className="border-t border-gray-200">
                      <td className="py-3 pr-4 font-semibold text-sm text-gray-800 align-top">
                        {categoryName}
                      </td>
                      <td className="py-3 text-center align-top">
                        <button
                          type="button"
                          onClick={() => handleSelection(panelIndex, categoryName, true)}
                          className={`py-1 px-3 rounded-l-full border font-semibold text-sm transition-all duration-200 ${
                            selections[panelIndex]?.[categoryName]?.present === true
                              ? 'bg-green-200 border-gray-600 border-2'
                              : 'bg-white border-gray-400'
                          }`}
                        >
                          Yes
                        </button>
                        <button
                          type="button"
                          onClick={() => handleSelection(panelIndex, categoryName, false)}
                          className={`py-1 px-3 rounded-r-full border font-semibold text-sm transition-all duration-200 ${
                            selections[panelIndex]?.[categoryName]?.present === false
                              ? 'bg-red-200 border-gray-600 border-2'
                              : 'bg-white border-gray-400'
                          }`}
                        >
                          No
                        </button>
                      </td>
                      <td className="py-3 align-top">
                        <AutoSaveCreatable
                          isMulti
                          isDisabled={!canEdit}
                          styles={SELECT_STYLES}
                          {...RISK_SELECT_PORTAL_PROPS}
                          options={(
                            selections[panelIndex]?.[categoryName]?.present
                              ? category.yesOptions
                              : category.noOptions
                          ).map((option) => ({ value: option, label: option }))}
                          value={selections[panelIndex]?.[categoryName]?.selectedOption || []}
                          onChange={(selectedOptions) => {
                            const predefinedValues = (
                              selections[panelIndex]?.[categoryName]?.present
                                ? category.yesOptions
                                : category.noOptions
                            );
                            const predefined = selectedOptions.filter((opt) => predefinedValues.includes(opt.value));
                            const custom = selectedOptions.filter((opt) => !predefinedValues.includes(opt.value));
                            const latestPredefined = predefined.length > 0
                              ? [predefined[predefined.length - 1]]
                              : [];
                            handleOptionChange(panelIndex, categoryName, [...latestPredefined, ...custom]);
                          }}
                          formatCreateLabel={(inputValue) => `Add "${inputValue}"`}
                          placeholder={canEdit ? 'Select an option and/or type your own…' : 'Selection can only be made by assigned clinician'}
                          className="basic-multi-select text-sm"
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );
      })}

      {/* Additional notes — same content as ADHD RSK, History panel styling */}
      <div className="bg-gray-50 rounded-lg border border-gray-300 text-left p-6 shadow-sm">
        <h4 className="font-bold text-lg mb-2">Additional Notes</h4>
        <p className="text-sm text-gray-600 mb-4">
          Please add any additional comments, observations or relevant information about
          risk factors not covered above.
        </p>
        <StructuredNotesEditor
          notes={additionalNotes}
          onChange={handleAdditionalNotesChange}
          domainId={RISK_ADDITIONAL_NOTES_PANEL_KEY}
          onMoveNoteIn={onMovePanelNote}
          moveTargets={noteMoveTargets}
          currentMoveId={RISK_ADDITIONAL_NOTES_PANEL_KEY}
          onMoveNoteTo={onMovePanelNote}
          placeholder="Add any additional comments, observations or relevant information…"
          readOnlyPlaceholder="Notes can only be added by the assigned clinician"
          readOnly={!canEdit}
          /* Only the CYP pathway passes noteTagOptions, and a catalogue
             without enableCriteriaTagging renders no Tag button at all — so
             the CYP source tags (parent / child / school) were unreachable
             here while the backend extractor already read them. Adult autism
             passes nothing and is unchanged. */
          {...(noteTagOptions ? { tagOptions: noteTagOptions, enableCriteriaTagging: true } : {})}
        />
      </div>
      </div>

      <AlertDialog.Root
        open={confirmDialogOpen}
        onOpenChange={(open) => {
          setConfirmDialogOpen(open);
          if (!open) setPendingToggle(null);
        }}
      >
        <AlertDialog.Portal>
          <AlertDialog.Overlay className="radix-dialog-overlay-motion fixed inset-0 bg-black/50 z-50" />
          <AlertDialog.Content className="radix-dialog-content-centered-motion fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white rounded-lg p-6 w-[420px] max-w-[90vw] z-50 shadow-xl">
            <AlertDialog.Title className="text-lg font-bold mb-2">
              Confirm Selection Change
            </AlertDialog.Title>
            <AlertDialog.Description className="text-sm text-gray-600 mb-5">
              {pendingToggle?.type === 'noToAll'
                ? 'Setting all categories to "No" will reset their details dropdowns to the default value, including any custom entries you\'ve added.'
                : 'Changing this toggle will reset the details dropdown for this category, including any custom entries you\'ve added.'}
            </AlertDialog.Description>
            <div className="flex justify-end gap-3">
              <AlertDialog.Cancel asChild>
                <button
                  type="button"
                  className="py-2 px-4 font-semibold border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
              </AlertDialog.Cancel>
              <AlertDialog.Action asChild>
                <button
                  type="button"
                  onClick={handleConfirmToggle}
                  className="py-2 px-4 font-semibold rounded-md bg-primary text-white hover:bg-primary/90 transition-colors"
                >
                  Continue
                </button>
              </AlertDialog.Action>
            </div>
          </AlertDialog.Content>
        </AlertDialog.Portal>
      </AlertDialog.Root>
    </div>
  );
}
